import React, { useState } from "react";
import { Modal } from "../../components/common/Modal";
import { useApp } from "../../app/context/AppContext";
import { useNotification } from "../../app/context/NotificationContext";
import { formatCurrency } from "../../utils/formatters";

export function PaymentModal({ isOpen, onClose }) {
  const { customers, suppliers, addPayment } = useApp();
  const { addToast } = useNotification();

  const [type, setType] = useState("Customer Receipt"); // 'Customer Receipt' | 'Supplier Payment'
  const [partyId, setPartyId] = useState(customers[0]?.id || "");
  const [amount, setAmount] = useState(25000);
  const [paymentMethod, setPaymentMethod] = useState("RTGS / NEFT");
  const [notes, setNotes] = useState("Partial settlement against monthly outstanding balance.");

  const activeParty =
    type === "Customer Receipt"
      ? customers.find((c) => c.id === partyId)
      : suppliers.find((s) => s.id === partyId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addPayment({
        type,
        partyId,
        partyName: activeParty?.name || "Party",
        amount: Number(amount),
        paymentMethod,
        notes,
        date: new Date().toISOString().slice(0, 10)
      });
      addToast(`Payment of ${formatCurrency(amount)} recorded for ${activeParty?.name}!`, "success");
      onClose();
    } catch (err) {
      addToast("Failed to record settlement payment.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record Financial Payment / Receipt"
      subtitle="Settle customer receivables or supplier payables"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Transaction Type *
          </label>
          <div className="grid grid-cols-2 gap-2">
            {["Customer Receipt", "Supplier Payment"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setType(t);
                  setPartyId(t === "Customer Receipt" ? customers[0]?.id : suppliers[0]?.id);
                }}
                className={`py-2 px-3 rounded-xl font-semibold border text-center transition ${
                  type === t
                    ? "bg-emerald-600 text-white border-emerald-600 shadow"
                    : "bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Select {type === "Customer Receipt" ? "Customer Account" : "Supplier / Vendor"} *
          </label>
          <select
            value={partyId}
            onChange={(e) => setPartyId(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          >
            {type === "Customer Receipt"
              ? customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} (Due: {formatCurrency(c.outstandingBalance)})
                  </option>
                ))
              : suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} (Due: {formatCurrency(s.outstandingBalance)})
                  </option>
                ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Settlement Amount (₹) *
            </label>
            <input
              type="number"
              min="1"
              required
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono font-bold text-emerald-600 dark:text-emerald-400"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Payment Mode *
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              <option value="RTGS / NEFT">RTGS / NEFT</option>
              <option value="Cheque / Clearing">Cheque / Clearing</option>
              <option value="UPI (GooglePay / PhonePe)">UPI (GooglePay / PhonePe)</option>
              <option value="Cash Counter">Cash Counter</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Notes / Bank Reference Number
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. UTR #482910482 or Cheque #88123"
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          />
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            Commit Settlement
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ExpenseModal({ isOpen, onClose }) {
  const { addExpense } = useApp();
  const { addToast } = useNotification();

  const [category, setCategory] = useState("Transportation & Freight");
  const [amount, setAmount] = useState(4500);
  const [paidTo, setPaidTo] = useState("Balaji Roadways");
  const [paymentMethod, setPaymentMethod] = useState("Bank NEFT");
  const [notes, setNotes] = useState("Local delivery tempo fuel & freight");
  const [approvedBy, setApprovedBy] = useState("Manager");

  const categories = [
    "Warehouse Rent",
    "Transportation & Freight",
    "Packaging Materials",
    "Electricity & Utilities",
    "Handling & Labor",
    "Salaries & Payroll",
    "Municipal & APMC Taxes",
    "Other Operational"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addExpense({
        category,
        amount: Number(amount),
        paidTo,
        paymentMethod,
        notes,
        approvedBy,
        date: new Date().toISOString().slice(0, 10)
      });
      addToast(`Expense of ${formatCurrency(amount)} logged under ${category}!`, "success");
      onClose();
    } catch (err) {
      addToast("Failed to log expense.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Operational Expense (OPEX)"
      subtitle="Categorize overhead costs for accurate P&L and net profit calculation"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Expense Category *
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Amount (₹) *
            </label>
            <input
              type="number"
              min="1"
              required
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono font-bold"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Paid To (Vendor / Entity) *
            </label>
            <input
              type="text"
              required
              value={paidTo}
              onChange={(e) => setPaidTo(e.target.value)}
              placeholder="e.g. MSEDCL, Logistics"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Description / Purpose
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          />
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            Record OPEX Expense
          </button>
        </div>
      </form>
    </Modal>
  );
}
